import React, { useCallback, useRef, useState } from 'react';

import { ActionIcon, Box, Button, Checkbox, Group, Menu } from '@mantine/core';
import { IconFilter2 } from '@tabler/icons-react';

import { ActiveDot } from '@/pages/common/ActiveDot';

import styles from './TableHeader.module.scss';

interface FilterMenuProps {
  selectedFilters: string[];
  showNoReportOption?: boolean;
  isNonPractitionerSignedCRSelected?: boolean;
  isPendingReviewSelectedCRSelected?: boolean;
  onNonPractitionerSignedCRToggle?: () => void;
  onApplyFilters: (
    filters: string[],
    isNonPractitionerSelected: boolean,
    isPendingReviewSelected: boolean,
    isRecentSigned?: boolean,
    isNotDownloaded?: boolean
  ) => void;
  showPreviousReportOptions?: boolean;
  isRecentSignedSelected?: boolean;
  isDownloadedSelected?: boolean;
}

const FilterMenuItem: React.FC<{
  value: string;
  label: string;
  isSelected: boolean;
  onToggle: () => void;
}> = ({ label, isSelected, onToggle }) => (
  <div className={`${styles.filterOption} ${isSelected ? styles.selectedOption : ''}`}>
    <label className={styles.filterCheckbox}>
      <Checkbox checked={isSelected} onChange={onToggle} mr={10} variant="outline" />
      <span className={styles.filterLabel}>{label}</span>
    </label>
  </div>
);

export const ReportFilterMenu: React.FC<FilterMenuProps> = ({
  selectedFilters = [],
  showNoReportOption = true,
  isNonPractitionerSignedCRSelected = false,
  isPendingReviewSelectedCRSelected = false,
  onApplyFilters,
  showPreviousReportOptions = false,
  isRecentSignedSelected = false,
  isDownloadedSelected = false
}) => {
  const [tempSelectedFilters, setTempSelectedFilters] = useState<string[]>(selectedFilters);
  const [tempNonPractitionerSelected, setTempNonPractitionerSelected] = useState(
    isNonPractitionerSignedCRSelected
  );
  const [tempRecentSignedSelected, setTempRecentSignedSelected] = useState(isRecentSignedSelected);
  const [tempDownloadedSelected, setTempDownloadedSelected] = useState(isDownloadedSelected);
  const [tempPendingReviewSelected, setTempPendingReviewSelected] = useState(
    isPendingReviewSelectedCRSelected
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const filterOptions = [
    { value: 'GREEN', label: 'Routine' },
    { value: 'YELLOW', label: 'Yellow Alert' },
    { value: 'RED', label: 'Red Alert' }
  ];

  // Check if any filters are active
  // Show dot only when filters are actually selected
  const hasActiveFilters =
    selectedFilters.length > 0 ||
    isNonPractitionerSignedCRSelected ||
    isPendingReviewSelectedCRSelected ||
    isRecentSignedSelected ||
    isDownloadedSelected;

  const handleFilterToggle = (filterType: string) => {
    setTempSelectedFilters((prev) =>
      prev.includes(filterType) ? prev.filter((f) => f !== filterType) : [...prev, filterType]
    );
  };

  const handleBooleanToggle = (
    type: 'nonPractitioner' | 'recentSigned' | 'notDownloaded' | 'pendingReview'
  ) => {
    switch (type) {
      case 'nonPractitioner':
        setTempNonPractitionerSelected((prev) => !prev);
        break;
      case 'recentSigned':
        setTempRecentSignedSelected((prev) => !prev);
        break;
      case 'notDownloaded':
        setTempDownloadedSelected((prev) => !prev);
        break;
      case 'pendingReview':
        setTempPendingReviewSelected((prev) => !prev);
        break;
    }
  };

  const handleApply = useCallback(() => {
    if (isApplying) return;

    setIsApplying(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      onApplyFilters(
        tempSelectedFilters,
        tempNonPractitionerSelected,
        tempPendingReviewSelected,
        tempRecentSignedSelected,
        tempDownloadedSelected
      );

      setTimeout(() => {
        setIsApplying(false);
        setIsMenuOpen(false);
      }, 200);
    }, 300);
  }, [
    tempSelectedFilters,
    tempNonPractitionerSelected,
    tempPendingReviewSelected,
    tempRecentSignedSelected,
    tempDownloadedSelected,
    onApplyFilters,
    isApplying
  ]);

  const handleCancel = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setTempSelectedFilters(selectedFilters);
    setTempNonPractitionerSelected(isNonPractitionerSignedCRSelected);
    setTempPendingReviewSelected(isPendingReviewSelectedCRSelected);
    setTempRecentSignedSelected(isRecentSignedSelected);
    setTempDownloadedSelected(isDownloadedSelected);
    setIsMenuOpen(false);
    setIsApplying(false);
  };

  const handleMenuOpen = () => {
    setTempSelectedFilters(selectedFilters);
    setTempNonPractitionerSelected(isNonPractitionerSignedCRSelected);
    setTempPendingReviewSelected(isPendingReviewSelectedCRSelected);
    setTempRecentSignedSelected(isRecentSignedSelected);
    setTempDownloadedSelected(isDownloadedSelected);
    setIsMenuOpen(true);
    setIsApplying(false);
  };

  const handleMenuClose = () => {
    if (!isApplying) {
      setIsMenuOpen(false);
      setIsApplying(false);
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Menu
      shadow="md"
      width={280}
      position="bottom-start"
      opened={isMenuOpen}
      onClose={handleMenuClose}
      closeOnItemClick={false}>
      <Menu.Target>
        <ActionIcon variant="subtle" size="sm" onClick={handleMenuOpen}>
          <Box pos="relative">
            <IconFilter2 size={14} />
            {hasActiveFilters && <ActiveDot sx={{ position: 'absolute', right: -2, top: -2 }} />}
          </Box>
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown className={styles.filtersMenu}>
        {filterOptions.map(({ value, label }) => (
          <FilterMenuItem
            key={value}
            value={value}
            label={label}
            isSelected={tempSelectedFilters.includes(value)}
            onToggle={() => !isApplying && handleFilterToggle(value)}
          />
        ))}

        {showNoReportOption && (
          <FilterMenuItem
            value="pending-physician-review"
            label="Pending Physician Review"
            isSelected={tempNonPractitionerSelected}
            onToggle={() => !isApplying && handleBooleanToggle('nonPractitioner')}
          />
        )}

        {showPreviousReportOptions && (
          <>
            <FilterMenuItem
              value="recent-signed"
              label="Recently Signed (last 30 days)"
              isSelected={tempRecentSignedSelected}
              onToggle={() => !isApplying && handleBooleanToggle('recentSigned')}
            />
            <FilterMenuItem
              value="not-downloaded"
              label="Not downloaded"
              isSelected={tempDownloadedSelected}
              onToggle={() => !isApplying && handleBooleanToggle('notDownloaded')}
            />
          </>
        )}

        <div className={styles.filterActions}>
          <Group gap="sm" justify="flex-end" p="6">
            <Button
              variant="subtle"
              size="xs"
              onClick={handleCancel}
              color="gray"
              disabled={isApplying}>
              Cancel
            </Button>
            <Button
              size="xs"
              onClick={handleApply}
              loading={isApplying}
              disabled={isApplying}
              loaderProps={{ size: 12 }}>
              Apply
            </Button>
          </Group>
        </div>
      </Menu.Dropdown>
    </Menu>
  );
};
